import { ServerFormFieldValidator, SessionFormField, SessionFormFieldDependency, SessionFormFieldsGroup } from '@uplift/api';
import { l10nEnService } from '@uplift/services';
import { isValidEmail } from '@uplift/validations';
import * as Yup from 'yup';


const calculateDependencyCondition = (value: unknown, dependency: SessionFormFieldDependency) => {
  return {
    eq: (v1: unknown, v2: unknown) => v1 === v2,
    not_eq: (v1: unknown, v2: unknown) => v1 !== v2,
    in: (v1: unknown, v2: unknown) => Array.isArray(v2) && v2.includes(v1),
    not_in: (v1: unknown, v2: unknown) => Array.isArray(v2) && v2.includes(v1),
  }[dependency.condition ?? 'eq'](value, dependency.value);
};

const generateValidatorHandler = <T extends Yup.Schema>(schema: T, validator: ServerFormFieldValidator): T => {
  switch (validator.type) {
    case 'one_of':
      schema = schema.oneOf(validator.values, validator.errorMessage);
      break;
  }
  return schema;
};

const generateBaseFieldValidationSchema = <T extends Yup.Schema>(schema: T, field: SessionFormField, group?: SessionFormFieldsGroup): Yup.Schema => {
  let result = Yup.mixed().nullable();

  schema = schema.nullable();

  if (!field.optional) {
    schema = schema.required(field.emptyFieldErrorMessage);
  }

  if (field.validators) {
    field.validators.forEach(validator => {
      schema = generateValidatorHandler(schema, validator);
    });
  }

  if (group?.dependency || field.dependency) {
    if (group?.dependency) {
      result = result.when(group.dependency.fieldId, {
        is: (value: unknown) => {
          return group.dependency ? calculateDependencyCondition(value, group.dependency) : false;
        },
        then: _ => schema,
      });
    }

    if (field.dependency) {
      result = result.when(field.dependency.fieldId, {
        is: (value: unknown) => {
          return field.dependency ? calculateDependencyCondition(value, field.dependency) : false;
        },
        then: _ => schema,
      });
    }
  } else {
    return schema;
  }

  return result;
};


const generateFieldValidationSchema = (field: SessionFormField | SessionFormFieldsGroup, group?: SessionFormFieldsGroup): Yup.Schema | null => {
  switch (field.fieldType) {
    case 'static_link':
    case 'static_label':
    case 'static_helper_text':
    case 'divider':
    case 'fields_group': {
      return null; // No validation needed for these types
    }
    case 'boolean': {
      const schema = Yup.boolean();

      return generateBaseFieldValidationSchema(schema, field, group);
    }
    case 'diagnosis': {
      const schema = Yup.array().of(Yup.string());

      return generateBaseFieldValidationSchema(schema, field, group);
    }
    case 'select_string': {
      const schema = Yup.string();

      return generateBaseFieldValidationSchema(schema, field, group);
    }
    case 'select_multiple_string': {
      let schema = Yup.array().of(Yup.string());

      if (field.optional === false) {
        schema = schema.min(1, 'At least 1 value required');
      }

      if (field.minimumValues) {
        schema = schema.min(field.minimumValues, `At least ${field.minimumValues} values required`);
      }

      return generateBaseFieldValidationSchema(schema, field, group);
    }
    case 'text_area': {
      const schema = Yup.string().min(field.minRows);

      if (field.minLength) {
        schema.min(field.minLength, `At least ${field.minLength} characters required`);
      }

      return generateBaseFieldValidationSchema(schema, field, group);
    }
    case 'text_field': {
      const schema = Yup.string();

      return generateBaseFieldValidationSchema(schema, field, group);
    }
    case 'date': {
      const schema = Yup.string();

      return generateBaseFieldValidationSchema(schema, field, group);
    }
    case 'integer': {
      const schema = Yup.number();

      return generateBaseFieldValidationSchema(schema, field, group);
    }
    case 'yes_or_other': {
      const schema = Yup.mixed();

      return generateBaseFieldValidationSchema(schema, field, group);
    }
    case 'email': {
      const schema = Yup.string().email('Invalid email format').test('ascii-email', l10nEnService.validations.validEmailOnly, isValidEmail);

      return generateBaseFieldValidationSchema(schema, field, group);
    }
    case 'phone_number': {
      const schema = Yup.string();

      return generateBaseFieldValidationSchema(schema, field, group);
    }
    case 'combo_box': {
      const schema = Yup.string();

      return generateBaseFieldValidationSchema(schema, field, group);
    }
  }
  return Yup.mixed().nullable();
};


const generateValidationDefinition = (fields: (SessionFormField | SessionFormFieldsGroup)[], group?: SessionFormFieldsGroup): Record<string, Yup.Schema> => {
  return fields?.reduce((validationDefinition, field) => {
    const schema = generateFieldValidationSchema(field, group);

    if (field.fieldType === 'fields_group') {
      const groupFieldsSchema = generateValidationDefinition(field.formFields || [], field);

      return {
        ...validationDefinition,
        ...groupFieldsSchema,
      };
    }

    if (!schema) return validationDefinition;

    return {
      ...validationDefinition,
      [field.id]: schema,
    };
  }, {} as Record<string, Yup.Schema>) ?? {};
};


export const generateValidationSchema = (fields: (SessionFormField | SessionFormFieldsGroup)[]) => {
  const fieldsSchema = generateValidationDefinition(fields);

  return Yup.object().shape(fieldsSchema);
};
